/** Super Admin — Alerts list with acknowledge/resolve. */
import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, ChevronRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import { Card, Modal, GhostBtn, PrimaryBtn, EmptyState, inputStyle, fieldLabel } from "./_atoms";

const SEVERITY = {
  critical: { bg: "rgba(239,68,68,0.10)", fg: "#EF4444", border: "rgba(239,68,68,0.35)" },
  warning: { bg: "rgba(245,158,11,0.10)", fg: "#D97706", border: "rgba(245,158,11,0.35)" },
  info: { bg: "rgba(42,40,37,0.06)", fg: "#4A4845", border: "rgba(12,12,11,0.10)" },
};

export default function SuperadminAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [resolveOf, setResolveOf] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient.get("/superadmin/dashboard/alerts").then((r) => setAlerts(r.data?.alerts || []));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60_000); return () => clearInterval(id); }, [load]);

  const ack = async (id) => {
    try {
      await apiClient.post(`/superadmin/alerts/${id}/acknowledge`);
      toast.success("Acknowledged");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resolve = async () => {
    if (!resolveOf) return;
    setBusy(true);
    try {
      await apiClient.post(`/superadmin/alerts/${resolveOf.id}/resolve`, { note });
      toast.success("Resolved");
      setResolveOf(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-alerts">
      <SectionHeader title="Alerts" onRefresh={load} />
      {alerts.length === 0 && <EmptyState title="All clear" sub="No actionable items right now." />}
      <div style={{ display: "grid", gap: 10 }}>
        {alerts.map((a) => {
          const s = SEVERITY[a.severity] || SEVERITY.info;
          return (
            <Card
              key={a.id}
              data-testid={`alert-${a.id}`}
              style={{ borderLeft: `3px solid ${s.fg}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 100, display: "grid", placeItems: "center",
                  background: s.bg, color: s.fg,
                }}>
                  <AlertTriangle size={16} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "var(--text-primary, #0C0C0B)" }}>{a.title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>{a.severity?.toUpperCase()}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {a.deeplink && (
                  <Link to={a.deeplink}>
                    <GhostBtn data-testid={`alert-open-${a.id}`}>Open <ChevronRight size={14} /></GhostBtn>
                  </Link>
                )}
                <GhostBtn onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}><Check size={14} /> Ack</GhostBtn>
                <PrimaryBtn onClick={() => setResolveOf(a)} data-testid={`alert-resolve-${a.id}`}>Resolve</PrimaryBtn>
              </div>
            </Card>
          );
        })}
      </div>

      <Modal open={!!resolveOf} onClose={() => setResolveOf(null)} title={`Resolve · ${resolveOf?.title || ""}`}>
        <label style={fieldLabel}>Resolution note (optional)</label>
        <textarea
          value={note} onChange={(e) => setNote(e.target.value)}
          style={{ ...inputStyle, height: 90, padding: "10px 14px" }}
          data-testid="alert-resolve-note"
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <GhostBtn onClick={() => setResolveOf(null)}>Cancel</GhostBtn>
          <PrimaryBtn onClick={resolve} disabled={busy} data-testid="alert-resolve-confirm">{busy ? "Saving…" : "Resolve"}</PrimaryBtn>
        </div>
      </Modal>
    </div>
  );
}
